
export enum ModuleType {
  DASHBOARD = 'Dashboard',
  JOB_PLANNING = 'Job Planning',
  SERVICE_CRM = 'Service CRM',
  SALES_CRM = 'Sales CRM',
  HSSE = 'HSSE',
  ASSET_MGR = 'Asset Manager',
  RENTAL_MGR = 'Rental Manager',
  HR_MGR = 'HR Manager',
  EXPENSE = 'Expense Tracker',
  CONTRACTOR = 'Contractor Mgr',
  DOC_CONTROL = 'Doc Control',
  SYSTEM_ADMIN = 'System Admin'
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
  status: string;
}

export interface KPI {
  label: string;
  value: string | number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: string;
  color: string;
}

// --- JOB PLANNING & EXECUTION TYPES ---

export type JobStatus = 'New' | 'Planned' | 'Allocated' | 'In Progress' | 'Completed' | 'Closed' | 'Cancelled';
export type JobPriority = 'Emergency' | 'High' | 'Medium' | 'Low';
export type JobType = 'Breakdown' | 'Planned Service' | 'Install' | 'Commissioning' | 'Rental Delivery' | 'Audit' | 'HSSE Action';

export interface JobPart {
  id: string;
  partNumber: string;
  description: string;
  qtyPlanned: number;
  qtyUsed: number;
  source: 'Stock' | 'Purchase' | 'Consignment';
}

export interface JobChecklistItem {
  id: string;
  label: string;
  status: 'Pass' | 'Fail' | 'N/A' | 'Pending';
  comment?: string;
}

export interface JobLog {
  id: string;
  type: 'Travel Start' | 'Arrived' | 'Work Start' | 'Work End' | 'Travel End';
  timestamp: string;
  user: string;
  gps?: string;
}

export interface QuoteRequest {
  id: string;
  description: string;
  photoUrl?: string;
  status: 'Pending' | 'Sent';
}

export interface Technician {
  id: string;
  name: string;
  skills: string[];
  status: 'Available' | 'Busy' | 'Leave';
  currentJobId?: string;
}

export interface Job {
  id: string;
  // Origin & Core Info
  origin: 'Service CRM' | 'Sales CRM' | 'Rental' | 'HSSE' | 'Manual';
  customer: string;
  site: string;
  machineId?: string; // Optional
  machineName?: string; // Snapshot for list view
  type: JobType;
  priority: JobPriority;
  status: JobStatus;
  
  // Dates
  dateCreated: string;
  plannedDate?: string;
  dueDate?: string; // SLA Target
  
  // Scope
  description: string;
  detailedScope?: string;
  
  // Allocation
  technician: string; // Primary Tech ID or Name
  assistant?: string;
  
  // Execution Data
  logs: JobLog[];
  parts: JobPart[];
  checklist: JobChecklistItem[];
  checklist20?: JobChecklistItem[]; // Standard 20-point check
  quoteRequests?: QuoteRequest[];
  
  // Tech Flow Timestamps
  travelStartTime?: string;
  arrivalTime?: string;
  jobStartTime?: string;
  jobEndTime?: string;

  // HSSE & Permits
  riskLevel?: 'Low' | 'Medium' | 'High';
  ptwStatus?: 'Not Required' | 'Draft' | 'Pending' | 'Approved' | 'Rejected';
  permitRequired?: boolean;
  permitId?: string;
  jhaCompleted?: boolean;
  
  // Closure
  customerSignOff?: string; // Name
  signatureUrl?: string;
  completionDate?: string;
  followUpRequired?: boolean;
}

// ... (Keep existing Interfaces for Incident, RiskAssessment, PermitToWork, MOC, Audit, ActionItem, DocControl, Asset, CRM)

export interface Incident {
  id: string;
  category: number;
  type: number; 
  severity: number;
  description: string;
  location: string;
  date: string;
  status: number;
  reportedBy: string;
}

export interface RiskAssessment {
  id: string;
  taskName: string;
  department: string;
  hazards: string[];
  initialRisk: string; // Sticking to string for risk matrix labels for now or could convert too
  controls: string[];
  residualRisk: string;
  reviewDate: string;
  status: number;
  ppe?: string[];
  teamMembers?: string;
  approvalDate?: string;
  approver?: string;
}

export interface PermitToWork {
  id: string;
  type: number;
  location: string;
  contractor: string;
  validFrom: string;
  validTo: string;
  status: number;
  authorizedBy?: string;
  scope?: string;
  tools?: string;
  equipment?: string;
  hazards?: string[];
  ppe?: string[];
  lotoRequired?: boolean;
  lotoNumber?: string;
  fireWatchRequired?: boolean;
  fireWatchName?: string;
  gasTestRequired?: boolean;
}

export interface MocApproval {
  role: string;
  name: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  date?: string;
}

export interface MocAction {
  id: string;
  task: string;
  assignee: string;
  dueDate: string;
  status: 'Pending' | 'Complete';
}

export interface ManagementOfChange {
  id: string;
  title: string;
  type: number;
  initiator: string;
  dateRaised: string;
  riskLevel: number;
  status: number;
  description?: string;
  justification?: string;
  impactAnalysis?: {
    drawingsUpdate?: boolean;
    proceduresUpdate?: boolean;
    trainingRequired?: boolean;
    regulatoryImpact?: boolean;
    envImpact?: boolean;
  };
  approvals?: MocApproval[];
  actions?: MocAction[];
}

export interface AuditInspection {
  id: string;
  type: number;
  auditor: string;
  date: string;
  score?: number;
  findings: number;
  status: number;
}

export interface ActionItem {
  id: string;
  source: number;
  description: string;
  reason?: string; 
  assignee: string; 
  raisedBy: string; 
  raisedDate: string;
  dueDate: string;
  priority: number;
  status: number;
  progressComments?: string;
  revisedDueDate?: string;
  completedDate?: string;
  evidence?: string;
  verifiedBy?: string;
  verificationDate?: string;
  closeOutStatus?: string;
}

export type DocStatus = 'Draft' | 'Review' | 'Pending Approval' | 'Active' | 'Archived';
export type DocCategory = 'Policy' | 'SOP' | 'Form' | 'Work Instruction' | 'Manual' | 'Drawing' | 'Legal';
export type Confidentiality = 'Public' | 'Internal' | 'Confidential' | 'Restricted';

export interface DocVersion {
  version: string;
  date: string;
  changedBy: string;
  changeSummary: string;
  fileUrl?: string;
}

export interface DocApproval {
  role: string;
  name: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  date?: string;
  comment?: string;
}

export interface TitanDocument {
  id: string;
  title: string;
  category: DocCategory;
  department: string;
  owner: string;
  currentVersion: string;
  status: DocStatus;
  history: DocVersion[];
  createdDate: string;
  lastReviewDate: string;
  nextReviewDate: string; 
  confidentiality: Confidentiality;
  approvals: DocApproval[];
  isoReference?: string;
  acknowledgmentRequired: boolean;
  acknowledgmentCount: number;
}

export type AssetCategory = 'Piston Compressor' | 'Screw Compressor' | 'Portable Compressor' | 'Air Receiver' | 'Air Dryer' | 'Inline Filter' | 'Other';

export interface Part {
  id: string;
  partNumber: string;
  description: string;
  quantity: number;
  isCritical: boolean;
  supplier?: string;
  cost?: number;
}

export interface Asset {
  id: string;
  tag: string; 
  name: string; 
  category: AssetCategory;
  application?: 'Workshop' | 'Plant Air' | 'Breathing Air' | 'Portable' | 'Standby';
  status: 'Active' | 'Standby' | 'Under Repair' | 'Decommissioned' | 'Scrap';
  siteId?: string;
  customerId?: string;
  location: string;
  make: string;
  model: string;
  configuration?: string;
  serialNumber: string;
  countryOrigin?: string;
  yearManufacture?: number;
  oemPartRef?: string;
  controllerType?: 'None' | 'Basic' | 'PLC' | 'VSD Controller';
  kw?: number;
  pressure?: number; 
  maxPressure?: number; 
  flow?: number; 
  stages?: number;
  dutyCycle?: 'Intermittent' | 'Continuous';
  coolingType?: 'Air' | 'Water';
  driveType?: 'Direct' | 'Belt' | 'Gear' | 'VSD';
  rpm?: number;
  airEndBrand?: string;
  airEndModel?: string;
  airendSerial?: string;
  oilInjected?: boolean;
  oilCapacity?: number; 
  oilType?: string;
  oilFilterType?: string;
  separatorType?: string;
  vsd?: boolean;
  inletValveType?: string;
  thermostaticValve?: string;
  cylinders?: number;
  pumpModel?: string;
  pumpRpm?: number;
  tankSize?: number; 
  crankcaseOilType?: string;
  beltSize?: string;
  pulleySize?: string;
  aftercoolerFitted?: boolean;
  intercoolerFitted?: boolean;
  voltage?: string;
  phase?: string; 
  frequency?: number; 
  fullLoadCurrent?: number; 
  startingMethod?: 'DOL' | 'Star-Delta' | 'Soft Starter' | 'VSD';
  motorMake?: string;
  motorModel?: string;
  motorSerial?: string;
  ipRating?: string;
  insulationClass?: string;
  efficiencyClass?: 'IE2' | 'IE3' | 'IE4';
  installDate?: string;
  installedBy?: string;
  environment?: 'Indoor' | 'Outdoor';
  ambientTempRange?: string;
  dustLevel?: 'Low' | 'Medium' | 'High';
  ventilation?: string;
  foundationType?: string;
  noiseLevel?: number; 
  noiseEnclosure?: boolean;
  dryerType?: 'Refrigerated' | 'Desiccant' | 'Membrane' | 'None';
  dryerModel?: string;
  receiverSize?: number;
  receiverPressure?: number;
  filtrationStages?: string; 
  breathingAirCompliant?: boolean;
  serviceInterval?: number; 
  hoursReading?: number;
  lastServiceDate?: string;
  lastServiceHours?: number;
  nextServiceDate?: string;
  nextServiceHours?: number;
  nextInspection: string; 
  serviceKitRef?: string;
  consumables?: string;
  lubricationSchedule?: string;
  warrantyStart?: string;
  warrantyEnd?: string;
  airEndWarranty?: number; 
  motorWarranty?: number; 
  slaLink?: string;
  extendedWarranty?: boolean;
  warrantyExclusions?: string;
  pressureVesselStandard?: 'SANS' | 'ASME' | 'CE';
  pressureCertUploaded?: boolean;
  pressureCertUrl?: string;
  lastPressureTestDate?: string;
  nextPressureTestDate?: string;
  reliefValvePressure?: number;
  reliefValveTestDate?: string;
  nextStatutoryInspection?: string;
  purchasePrice?: number;
  installCost?: number;
  expectedLifespan?: number;
  energyConsumption?: number; 
  replacementYear?: number;
  notes?: string;
  criticality?: 'High' | 'Medium' | 'Low';
  material?: string;
  billOfMaterials?: Part[];
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'Accounts Payable' | 'Maintenance' | 'Buyer' | 'Site Supervisor' | 'HSE' | 'Other';
  whatsapp?: string;
  preferredMethod?: 'Email' | 'Phone' | 'WhatsApp';
  isPrimary: boolean;
}

export interface Customer {
  id: string;
  name: string;
  tradingName?: string;
  regNumber?: string;
  type: 'Prospect' | 'Active' | 'Dormant' | 'Blacklisted'; 
  status: 'Prospect' | 'Active' | 'Dormant' | 'Blacklisted'; 
  accountOwner?: string;
  website?: string;
  billingAddress: string;
  physicalAddress?: string;
  deliveryAddress?: string;
  gps?: string;
  industry: string;
  accountCode?: string;
  vat?: string;
  paymentTerms?: 'COD' | '7 Days' | '15 Days' | '30 Days';
  creditLimit?: number;
  pricingTier?: 'Retail' | 'Wholesale' | 'Key Account';
  discount?: number;
  invoicingMethod?: 'Email' | 'Portal' | 'Printed';
  invoiceEmail?: string;
  poRequired?: boolean;
  bankReference?: string;
  contacts: Contact[];
  mainContact: { name: string; email: string; phone: string }; 
  leadSource?: string;
  priority?: 'A' | 'B' | 'C';
  expectedSpend?: number;
  territory?: string;
  competitors?: string;
  nextFollowUp?: string;
  slaLevel?: string; 
  responseRequirement?: string; 
  accessRules?: string;
  safetyNotes?: string;
  technicianNotes?: string;
  group?: string;
  activeSites: number;
  marketingConsent?: string[];
  newsletter?: boolean;
}

export interface SiteContact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  whatsapp?: string;
  isPrimary: boolean;
}

export interface Site {
  id: string;
  customerId: string;
  name: string;
  siteCode?: string;
  type?: 'Factory' | 'Workshop' | 'Service Station' | 'Warehouse' | 'Plant Room' | 'Mine' | 'Other';
  status: 'Active' | 'Inactive' | 'Decommissioned';
  address: string;
  city: string;
  region?: string;
  gps?: string;
  accessInstructions?: string;
  gateCode?: string;
  workingHours?: string;
  afterHoursAccess?: boolean;
  contacts: SiteContact[];
  contact: { name: string; email: string; phone: string }; 
  hsseRequirements: string; 
  riskCategory?: 'Low' | 'Medium' | 'High';
  inductionRequired?: boolean;
  permitsRequired?: string[]; 
  ppeRequired?: string[]; 
  specialHazards?: string;
  emergencyAssemblyPoint?: string;
  nearestHospital?: string;
  servicePriority?: 'Standard' | 'Critical' | '24/7';
  slaLink?: string;
  responseRequirement?: string;
  powerSupply?: string;
  airDemand?: string;
  environment?: 'Indoor' | 'Outdoor' | 'Harsh' | 'Clean Room';
  billTo?: 'Head Office' | 'Site';
  poRequired?: boolean;
  costCentre?: string;
}

export interface ConnectedItem {
  id: string;
  machineId: string;
  type: 'Line Filter' | 'Dryer' | 'Receiver' | 'Separator' | 'Nitrogen Gen';
  make: string;
  model: string;
  serial?: string;
  status: 'Active' | 'Spare' | 'Removed';
}

export interface ServiceRecord {
  id: string;
  machineId: string;
  date: string;
  type: 'Major' | 'Minor' | 'Breakdown' | 'Inspection' | 'Install';
  hours: number;
  technician: string;
  summary: string;
  jobId: string;
}

export type ExpenseCategory = 'Fuel' | 'Travel' | 'Accommodation' | 'Meals' | 'Parts/Spares' | 'Consumables' | 'Tolls' | 'Entertainment' | 'Office Supplies' | 'Other';
export type PaymentMethod = 'Company Card' | 'Cash (Pocket)' | 'EFT';
export type ExpenseStatus = 'Draft' | 'Submitted' | 'Manager Approved' | 'Rejected' | 'Finance Approved' | 'Paid';

export interface ExpenseTransaction {
  id: string;
  date: string;
  merchant: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  category: ExpenseCategory;
  description: string;
  jobId?: string;
  customerId?: string;
  costCentre?: string;
  isBillable: boolean;
  receiptUrl?: string;
  vatAmount?: number;
  vatVendor?: boolean;
  status: ExpenseStatus;
  submittedBy: string;
  approvedBy?: string;
  approvalDate?: string;
  financeComment?: string;
  isCardFeed?: boolean; 
  bankTransactionId?: string;
}

export interface Budget {
  department: string;
  monthlyLimit: number;
  currentSpend: number;
  year: number;
  month: number;
}

// --- RENTAL MODULE TYPES ---

export type RentalStatus = 'Available' | 'On Rent' | 'Reserved' | 'Maintenance' | 'Transit' | 'Sold';
export type BillingCycle = 'Daily' | 'Weekly' | 'Monthly' | 'Project';

export interface RentalAsset {
  id: string;
  assetId: string; // Link to base Asset/Machine
  fleetNumber: string;
  make: string;
  model: string;
  type: string; // e.g. Diesel Portable, Electric Screw
  specs: string; // e.g. 190CFM, 7Bar
  status: RentalStatus;
  depot: string;
  currentLocation?: string; // If on rent
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  bookValue: number;
  purchaseDate: string;
  utilizationYTD: number; // Percentage
  linkedContractId?: string;
}

export interface RentalQuote {
  id: string;
  customer: string;
  site: string;
  date: string;
  items: { assetType: string, qty: number, rate: number, cycle: BillingCycle }[];
  totalEstimated: number;
  status: 'Draft' | 'Sent' | 'Accepted' | 'Declined' | 'Converted';
  validUntil: string;
}

export interface RentalContract {
  id: string;
  quoteId?: string;
  customer: string;
  site: string;
  startDate: string;
  endDate?: string; // Estimated or Actual
  status: 'Active' | 'Completed' | 'Terminated' | 'Pending Delivery';
  billingCycle: BillingCycle;
  depositAmount?: number;
  assets: RentalAsset[]; // Which actual assets are on this contract
  deliveryJobId?: string;
  collectionJobId?: string;
}

export interface RentalInspection {
  id: string;
  contractId: string;
  assetId: string;
  type: 'On-Hire' | 'Off-Hire';
  date: string;
  inspector: string;
  hours: number;
  fuelLevel: string;
  conditionRating: number; // 1-5
  notes: string;
  photos: string[];
  passed: boolean;
}

// --- SALES CRM TYPES ---

export type LeadStage = 'Lead' | 'Qualified' | 'Site Visit' | 'Quote' | 'Negotiation' | 'Won' | 'Lost';

export interface SalesActivity {
    id: string;
    type: 'Call' | 'Meeting' | 'Email' | 'Site Visit' | 'Quote Sent';
    date: string;
    leadId?: string; // Link to lead if applicable
    leadName?: string;
    notes: string;
    outcome?: string;
    loggedBy: string;
}

export interface SalesTarget {
  id: string;
  repId: string; // 'Global' for company level
  repName: string;
  period: string; // e.g. 'May 2024' or 'Q2 2024'
  visitTarget: number;
  callTarget: number;
  revenueTarget: number;
  dealsClosedTarget: number;
  // Actuals for progress tracking
  actualVisits?: number;
  actualCalls?: number;
  actualRevenue?: number;
  actualDeals?: number;
}

export interface SalesLead {
  id: string;
  company: string;
  contactPerson: string;
  email: string;
  phone: string;
  
  // Commercial
  value: number; // Total estimated value
  productMix?: { equipment: number, service: number, spares: number }; 
  gpMargin?: number;
  
  // Pipeline Status
  stage: LeadStage;
  probability: number; // 0-100%
  expectedCloseDate?: string;
  
  // Metadata
  source: string; // Web, Cold Call, Referral
  region: string; // JHB, DBN, CPT
  temperature: 'Hot' | 'Warm' | 'Cold';
  owner: string; // Sales Rep
  
  // Follow Up
  nextAction?: string;
  nextActionDate?: string;
  
  // History
  activities: SalesActivity[];
  
  // Lost Logic
  competitor?: string;
  lostReason?: string;
}

// --- CONTRACTOR MANAGEMENT TYPES ---

export type ContractorGrade = 'A' | 'B' | 'C' | 'Reject';
export type RiskLevel = 'Low' | 'Medium' | 'High';
export type DocType = 'COIDA' | 'Public Liability' | 'B-BBEE' | 'HSE Plan' | 'ISO 9001' | 'Tax Clearance' | 'Other';

export interface ContractorDocument {
  id: string;
  type: DocType;
  expiryDate: string;
  status: 'Valid' | 'Expiring Soon' | 'Expired' | 'Missing';
  fileUrl?: string;
  verifiedBy?: string;
}

export interface ContractorEmployee {
  id: string;
  name: string;
  role: string;
  competencies: string[]; // e.g. 'Working at Heights', 'Electrician', 'First Aid'
  certificates?: { name: string, expiry: string }[];
}

export interface PerformanceScore {
  jobId: string;
  date: string;
  hsseScore: number; // 0-100
  qualityScore: number;
  scheduleScore: number;
  averageScore: number;
  rater: string;
}

export interface Contractor {
  id: string;
  name: string;
  tradingName?: string;
  regNumber: string;
  vatNumber?: string;
  status: 'Active' | 'Probation' | 'Suspended' | 'Blacklisted' | 'Pending';
  riskLevel: RiskLevel;
  grade: ContractorGrade;
  prequalScore: number; // 0-100 from pre-qualification
  trades: string[]; // e.g. 'Electrical', 'Rigging'
  documents: ContractorDocument[];
  employees: ContractorEmployee[];
  performanceHistory: PerformanceScore[];
  contactPerson: string;
  email: string;
  phone: string;
}

// --- HR & TRAINING TYPES ---

export type HRDepartment = 'Operations' | 'Sales' | 'Finance' | 'HR' | 'HSSE' | 'Workshop' | 'Management';
export type LeaveType = 'Annual' | 'Sick' | 'Family Responsibility' | 'Maternity' | 'Study' | 'Unpaid';
export type LeaveStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
export type OvertimeStatus = 'Pending' | 'Approved' | 'Rejected' | 'Paid';

export interface Attachment {
  id: string;
  name: string;
  url: string;
  uploadDate: string;
  type: 'Certificate' | 'Review' | 'Scorecard' | 'Evidence' | 'Other' | 'License' | 'Medical';
}

export interface ScorecardItem {
  id: string;
  category: string; // e.g. "Financial", "Customer", "Internal Process"
  criteria: string; // What is being measured
  methodology: string; // How it is measured
  weight: number;
  target: string;
  actual: string;
  score: number; // 1-5
  evidence: Attachment[];
}

export interface PDPGoal {
  id: string;
  objective: string;
  actionPlan: string; // Methodology for improvement
  targetDate: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  evidence: Attachment[];
}

export interface PPEItem {
  id: string;
  item: string; // e.g., 'Safety Boots', 'Hard Hat'
  issueDate: string;
  expiryDate: string; // Calculated based on lifecycle
  status: 'Valid' | 'Expiring' | 'Expired';
}

export interface Competency {
  id: string;
  name: string; // e.g., 'Working at Heights', 'Forklift License'
  issueDate: string;
  expiryDate: string;
  status: 'Valid' | 'Expiring' | 'Expired';
  requiredForRole: boolean;
  documents: Attachment[]; // Certificates
}

export interface PerformanceReview {
  id: string;
  period: string; // e.g., 'FY2024'
  date: string;
  reviewer: string;
  rating: number; // 1-5
  managerComments: string;
  scorecard: ScorecardItem[];
  pdp: PDPGoal[];
  documents: Attachment[]; // Signed complete review document
}

export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  days: number;
  reason?: string;
  status: LeaveStatus;
  approver?: string;
}

export interface OvertimeEntry {
  id: string;
  employeeId: string;
  date: string;
  hours: number;
  jobId?: string; // Linked to Job Card
  reason: string;
  status: OvertimeStatus;
  approver?: string;
}

export interface DriversLicense {
  code: string; // B, C1, EC etc
  number: string;
  issueDate: string;
  expiryDate: string;
  prdpExpiry?: string;
  restrictions?: string;
  document?: Attachment;
}

export interface MedicalRecord {
  status: 'Fit' | 'Fit with Restrictions' | 'Unfit';
  examDate: string;
  expiryDate: string;
  restrictions?: string;
  allergies?: string;
  conditions?: string;
  emergencyInstructions?: string;
  document?: Attachment;
}

export interface ClothingSizes {
  shirt: string;
  trousers: string;
  overalls: string;
  safetyShoes: string;
  jacket: string;
  gloves: string;
}

export interface NextOfKin {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  address?: string;
}

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  department: HRDepartment;
  startDate: string;
  status: 'Active' | 'On Leave' | 'Terminated' | 'Probation';
  managerId?: string;
  managerName?: string;
  
  // Extended Personal Data
  idNumber: string;
  passportNumber?: string;
  dob: string;
  gender: 'Male' | 'Female' | 'Other';
  nationality: string;
  address: string;
  employmentType: 'Permanent' | 'Fixed-Term' | 'Contractor';
  
  // New Sub-Objects
  driversLicense?: DriversLicense;
  medical?: MedicalRecord;
  clothingSizes?: ClothingSizes;
  nextOfKin?: NextOfKin[];

  // Existing Sub-modules
  leaveBalance: number;
  competencies: Competency[];
  ppeIssued: PPEItem[];
  performanceHistory: PerformanceReview[];
}
